import { Component, Input } from '@angular/core';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { SelectListItemModel } from 'models/SelectList';
import { CrayonProductModel } from 'models/generated/Distribution.Api.Models.Csp.CrayonProduct';
import { SubscriptionModel } from 'models/generated/Distribution.Api.Models.Csp.Subscription';
import { MicrosoftSubscriptionModel } from 'models/generated/Distribution.Api.Models.MicrosoftSubscription';
import { UpdateSubscriptionInitialOrderDataRequestModel } from 'models/generated/Distribution.Api.Models.UpdateSubscriptionInitialOrderDataRequest';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { CspService } from 'services/csp.service';
import { NotificationService } from 'services/notification.service';

@Component({
	selector: 'company-microsoft365-edit',
	templateUrl: './company-microsoft365-edit.component.html',
	imports: [Layout, CustomSelectComponent, TranslatePipe, ProductNamePipe]
})
@CredentialRequired(CredentialType.EditMicrosoft365)
export class CompanyMicrosoft365EditComponent {

	crayonProductsSelectList: SelectListItemModel[] = [];
	selectedPartNumber?: string;
	selectedSubscriptionProductId: string = '';
	selectedSubscriptionDisplayValue: string = '';
	inputTypeEnum = InputType;
	credentialType = CredentialType;
	loading = false;

	private _productPlanSubscription?: ProductPlanSubscriptionsViewModel;
	@Input() set productPlanSubscription(pp: ProductPlanSubscriptionsViewModel | undefined) {
		this._productPlanSubscription = pp;
		if (pp) this.findSubscriptionInSelectList();
	}
	get productPlanSubscription(): ProductPlanSubscriptionsViewModel {
		return this._productPlanSubscription!;
	}

	private _crayonProducts: CrayonProductModel[] = [];
	@Input() set crayonProducts(crayonProducts: CrayonProductModel[]) {
		this._crayonProducts = crayonProducts;
		this.loading = crayonProducts.length === 0;
		this.crayonProductsSelectList = [];
		crayonProducts.forEach(product => {
			this.crayonProductsSelectList.push(new SelectListItemModel(product.id.toString(), product.productName));
		});
		if (this.productPlanSubscription) this.findSubscriptionInSelectList();
	}
	get crayonProducts() {
		return this._crayonProducts;
	}
	@Input() customerTenantId!: number;

	constructor(private readonly cspService: CspService, private readonly accountingService: AccountingService, private readonly notificationService: NotificationService) { }

	startPlan() {
		if (this.isReadyToStartPlan())
			this.registerStartOfPlan();
		else
			this.notificationService.warning('microsoft365.plan-not-ready-for-start');
	}

	findSubscriptionInSelectList() {
		if (this.productPlanSubscription.subscription?.initialOrderData && this.crayonProducts.length !== 0) {
			const initialOrderData = JSON.parse(this.productPlanSubscription.subscription.initialOrderData);
			const match = this.crayonProducts.find(p => p.partNumber === initialOrderData.partNumber);
			this.selectedSubscriptionProductId = match?.id.toString() ?? '';
			this.selectedSubscriptionDisplayValue = match?.productName ?? '';
		} else {
			this.selectedSubscriptionProductId = '';
			this.selectedSubscriptionDisplayValue = '';
		}
	}

	updateSubscriptionInitialOrderData(productId: string) {
		const selectedProduct = this.crayonProducts.find(p => p.id.toString() === productId);

		if (!selectedProduct) return;
		this.selectedSubscriptionProductId = productId;
		this.selectedSubscriptionDisplayValue = selectedProduct.productName;

		let initialOrderData = new UpdateSubscriptionInitialOrderDataRequestModel();
		initialOrderData.productPlanId = this.productPlanSubscription!.productPlan.id;
		initialOrderData.customerTenantId = this.customerTenantId;
		initialOrderData.billingCycleType = selectedProduct.defaultBillingCycleId;
		initialOrderData.partNumber = selectedProduct.partNumber;
		initialOrderData.termDuration = 'P1Y';

		this.cspService.updateSubscriptionInitialOrderData(initialOrderData);
	}

	private async registerStartOfPlan() {
		if (!this.isReadyToStartPlan()) return;

		this.notificationService.confirm('microsoft365.force-start-of-plan.confirm-popup', { productPlanName: this.productPlanSubscription.productPlan.productName }).subscribe(async result => {
			if (result) {
				this.loading = true;
				const skippedPlans = await this.accountingService.registerStartOfPlanByProductPlanId(this.productPlanSubscription.productPlan.id!);
				this.loading = false;

				if (skippedPlans.length == 0) return;
				for (const skippedPlan of skippedPlans) {
					this.notificationService.warning(`validation-error.${skippedPlan.warningCode}`);
				}
			}
		});
	}

	isReadyToStartPlan(): boolean {
		return !!this.productPlanSubscription.subscription.initialOrderData && !this.productPlanSubscription.subscription.newCommerceOrderId && !this.loading;
	}
}

export interface ProductPlanSubscriptionsViewModel {
	productPlan: ProductPlanModel;
	subscription: MicrosoftSubscriptionModel;
	crayonSubscription?: SubscriptionModel;
	status: string;
}
